'use client';

import { useCallback, useState, useEffect } from 'react';
import clsx from 'clsx';

import {
  Pagination as BasePagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/slw/shared/ui/pagination';
import { cn } from '@/slw/shared/lib/utils';

export interface PageChangeDataProps {
  page: number;
}

interface PaginationProps {
  className?: string;

  /**
   * Default: 1
   */
  currentPage?: number;

  /*
   * Default: 20
   */
  pageSize?: number;

  /**
   * Default: 0
   */
  totalItems?: number;

  onPageChange?: (data: PageChangeDataProps) => void;
}

export function Pagination(props: Readonly<PaginationProps>) {
  const [currentPage, setCurrentPage] = useState(props.currentPage || 1);
  const pageSize = props.pageSize || 20;
  const totalItems = props.totalItems || 0;
  const totalPage = Math.ceil(totalItems / pageSize);

  const handlePageChange = useCallback(
    (page: number) => () => {
      if (currentPage === page || page > totalPage || totalPage === 0 || page === 0) {
        return;
      }

      setCurrentPage(page);
      props.onPageChange && props.onPageChange({ page });
    },
    [currentPage, props, totalPage],
  );

  // Force update current page from parent
  useEffect(() => {
    if (!props.currentPage || props.currentPage === currentPage) return;

    setCurrentPage(props.currentPage);
  }, [props.currentPage, currentPage]);

  const isMobile = false; // TODO handle
  const pages = isMobile ? getPageListMobile(currentPage, totalPage) : getPageList(currentPage, totalPage);

  return (
    <BasePagination className={cn('justify-start', props.className)}>
      <PaginationContent>
        <PaginationItem className='h-8 bg-transparent rounded-md border border-[#EBE9E5]'>
          <PaginationPrevious
            className='w-8 h-8 bg-transparent text-default'
            onClick={currentPage === 1 ? () => {} : handlePageChange(currentPage - 1)}
          />
        </PaginationItem>
        {pages.map((page, idx) => {
          if (page === null) {
            return (
              <PaginationItem key={`${page}_${idx}`}>
                <PaginationEllipsis
                  className='w-8 h-8 bg-transparent text-default
                rounded-md border border-[#EBE9E5]'
                />
              </PaginationItem>
            );
          }

          return (
            <PaginationItem key={`${page}_${idx}`}>
              <PaginationLink
                isActive={page === currentPage}
                onClick={handlePageChange(page)}
                className={clsx(
                  'w-8 h-8 bg-transparent text-default rounded-md border border-[#EBE9E5] hover:text-background!',
                  {
                    'bg-primary-3 text-background': page === currentPage,
                  },
                )}
              >
                {page}
              </PaginationLink>
            </PaginationItem>
          );
        })}
        <PaginationItem>
          <PaginationNext
            className='w-8 h-8 bg-transparent text-default rounded-md border border-[#EBE9E5]'
            onClick={currentPage === totalPage || totalPage === 0 ? () => {} : handlePageChange(currentPage + 1)}
          />
        </PaginationItem>
      </PaginationContent>
    </BasePagination>
  );
}

function getPageList(currentPage: number, totalPage: number) {
  if (getCenterPages(currentPage, totalPage)) {
    const pages: number[] = [];

    for (let i = 1; i <= totalPage; i++) {
      pages.push(i);
    }

    return pages;
  }

  if (currentPage === 1) {
    return [1, 2, 3, null, totalPage];
  }

  if (currentPage === 2) {
    return [1, 2, 3, 4, null, totalPage];
  }

  if (currentPage === 3) {
    return [1, 2, 3, 4, 5, null, totalPage];
  }

  if (currentPage === 4) {
    return [1, 2, 3, 4, 5, 6, null, totalPage];
  }

  if (currentPage === totalPage) {
    return [1, null, totalPage - 2, totalPage - 1, totalPage];
  }

  if (currentPage === totalPage - 1) {
    return [1, null, totalPage - 3, totalPage - 2, totalPage - 1, totalPage];
  }

  if (totalPage > 6) {
    if (currentPage === totalPage - 2) {
      return [1, null, totalPage - 4, totalPage - 3, totalPage - 2, totalPage - 1, totalPage];
    }

    if (currentPage === totalPage - 3) {
      return [1, null, totalPage - 5, totalPage - 4, totalPage - 3, totalPage - 2, totalPage - 1, totalPage];
    }
  }

  return [1, null, currentPage - 2, currentPage - 1, currentPage, currentPage + 1, currentPage + 2, null, totalPage];
}

function getCenterPages(currentPage: number, totalPage: number) {
  return (
    totalPage <= 5 ||
    (totalPage === 6 && (currentPage === 3 || currentPage === 4)) ||
    (totalPage === 7 && currentPage === 4)
  );
}

function getPageListMobile(currentPage: number, totalPage: number) {
  if (getCenterPagesMobile(totalPage)) {
    const pages: number[] = [];

    for (let i = 1; i <= totalPage; i++) {
      pages.push(i);
    }

    return pages;
  }

  if (currentPage === 1 || currentPage === 2) {
    return [1, 2, 3, null, totalPage];
  }

  if (currentPage === 2) {
    return [1, 2, 3, null, totalPage];
  }

  if (currentPage === 3) {
    return [1, 2, 3, 4, null, totalPage];
  }

  if (currentPage === totalPage) {
    return [1, null, totalPage - 1, totalPage];
  }

  if (currentPage === totalPage - 1) {
    return [1, null, totalPage - 2, totalPage - 1, totalPage];
  }

  if (totalPage > 3) {
    if (currentPage === totalPage - 2) {
      return [1, null, totalPage - 3, totalPage - 2, totalPage - 1, totalPage];
    }
  }

  return [1, null, currentPage - 1, currentPage, currentPage + 1, null, totalPage];
}

function getCenterPagesMobile(totalPage: number) {
  return totalPage <= 5;
}
